/**
 * Revision-submission policy — ONE home (2026-09-01, Fredrik), consumed by
 * both mount points of the flow: the My Submissions rows (ProducerDashboard)
 * and the in-cycle SubmitRevisionControl. The rule that lives here: a
 * revision must be the SAME FORMAT as the version it replaces — the
 * channel's format rules were settled at first submit; a different format
 * is new content, not a revision. Mirrors
 * fileValidationService.detectContentType on the backend.
 */

export const detectRevisionType = (fileName: string): string | null => {
  const ext = (fileName.match(/\.[^/.]+$/) || [''])[0].toLowerCase();
  const map: Record<string, string> = {
    '.docx': 'docx', '.pptx': 'pptx', '.pdf': 'pdf',
    '.mp4': 'video', '.mov': 'video', '.avi': 'video', '.webm': 'video',
    '.png': 'image', '.jpg': 'image', '.jpeg': 'image', '.gif': 'image', '.svg': 'image', '.webp': 'image'
  };
  return map[ext] || null;
};

export const revisionTypeLabel = (t: string | null) =>
  t === 'pptx' ? 'a PowerPoint' : t === 'docx' ? 'a Word document' : t === 'pdf' ? 'a PDF'
    : t === 'image' ? 'an image' : t === 'video' ? 'a video' : 'an unsupported format';

export const formatMismatchMessage = (fileName: string, picked: string | null, targetName: string, targetType: string) =>
  `"${fileName}" is ${revisionTypeLabel(picked)}, but "${targetName}" is ${revisionTypeLabel(targetType)}. ` +
  `A revision must be the same format as the version it replaces — export it as ${targetType.toUpperCase()} ` +
  `and try again, or submit it as new content.`;

export const reentryText = (reentry: string | undefined) =>
  reentry === 'dm' ? 'goes straight to the decision maker' : 're-enters review';

/**
 * Video/image working material is filed BY HAND (the D81 matrix makes these
 * link-only — there is no single editable file for the pipeline to carry),
 * so a revision leaves the hand-drop folder holding the previous version's
 * source files until the producer updates it. Both revision surfaces append
 * this to their success message (2026-09-01, Fredrik).
 */
export const workingMaterialReminder = (contentType: string | null | undefined): string =>
  contentType === 'video' || contentType === 'image'
    ? ` One manual step remains: ${contentType} working material is not filed automatically — update this content's folder under "Approved content – working files/${contentType}/" so the source files (project files, voiceover, transcripts, …) match the version you just submitted.`
    : '';

/** A companion (a pdf's editable master) must itself be editable. */
export const isEditableCompanion = (fileName: string): boolean =>
  /\.(docx|pptx|xlsx)$/i.test(fileName);

/**
 * Where the producer should START a revision from (2026-09-01, Fredrik):
 * the working-files tree holds the current editable master, carrying every
 * previously applied fix — editing from an old local copy silently drops
 * that round's changes.
 */
export const startFromWorkingFileHint = (contentType: string | null | undefined): string => {
  if (contentType === 'docx' || contentType === 'pptx' || contentType === 'xlsx') {
    return 'Start from the editable file in "Approved content – working files" — it carries the previously applied fixes.';
  }
  if (contentType === 'pdf') {
    return 'Start from the editable working file (companion) in "Approved content – working files", then export the new PDF from it.';
  }
  return '';
};
